"use client"

import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react"

import type {
  FooterRebrushContext,
  FooterRebrushVisibility,
} from "@/components/storyblok/footer-rebrush/footer-rebrush-types"
import {
  hasFooterRebrushContextVisibility,
  isFooterRebrushVisible,
} from "@/components/storyblok/footer-rebrush/footer-rebrush-visibility"

interface StorefrontContextResponse {
  isB2B: boolean
  oci: boolean
}

interface FooterRebrushVisibilityContextValue {
  context?: FooterRebrushContext
  loading: boolean
}

const FooterRebrushVisibilityContext = createContext<FooterRebrushVisibilityContextValue>({
  loading: true,
})

export function FooterRebrushVisibilityProvider({
  sbLocale,
  initialContext,
  children,
}: PropsWithChildren<{
  sbLocale?: string
  initialContext: FooterRebrushContext
}>) {
  const [context, setContext] = useState<FooterRebrushContext | undefined>()

  useEffect(() => {
    const controller = new AbortController()
    const contextUrl = sbLocale
      ? `/api/storefront/context?sbLocale=${encodeURIComponent(sbLocale)}`
      : "/api/storefront/context"

    fetch(contextUrl, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((storefrontContext: StorefrontContextResponse | undefined) => {
        if (!storefrontContext) {
          setContext(initialContext)
          return
        }

        setContext({
          country: initialContext.country,
          isB2B: storefrontContext.isB2B,
          isOCI: storefrontContext.oci,
        })
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load storefront context for footer rebrush visibility", error)
          setContext(initialContext)
        }
      })

    return () => controller.abort()
  }, [initialContext, sbLocale])

  const value = useMemo(
    () => ({
      context,
      loading: !context,
    }),
    [context]
  )

  return <FooterRebrushVisibilityContext.Provider value={value}>{children}</FooterRebrushVisibilityContext.Provider>
}

export function FooterRebrushVisibilityGate({
  visibility,
  children,
}: PropsWithChildren<{
  visibility?: FooterRebrushVisibility
}>) {
  const { context, loading } = useContext(FooterRebrushVisibilityContext)

  if (!hasFooterRebrushContextVisibility(visibility)) {
    return <>{children}</>
  }

  if (loading || !context || !isFooterRebrushVisible(visibility, context)) {
    return null
  }

  return <>{children}</>
}

export function FooterRebrushAnyVisible({
  visibilityItems,
  children,
}: PropsWithChildren<{
  visibilityItems: (FooterRebrushVisibility | undefined)[]
}>) {
  const { context, loading } = useContext(FooterRebrushVisibilityContext)
  const dynamicItems = visibilityItems.filter(hasFooterRebrushContextVisibility)

  if (dynamicItems.length === 0) {
    return <>{children}</>
  }

  if (loading || !context || !visibilityItems.some((item) => isFooterRebrushVisible(item, context))) {
    return null
  }

  return <>{children}</>
}
